import type { SiteContent } from '../content/siteContent';
import { getItems, getText } from '../content/cmsAccess';
import { toSeoImageSrcSet, toSeoImageUrl } from './seoImages';

export type SeoConfig = {
  title: string;
  description: string;
  keywords: string;
  image: string;
  imageAlt: string;
  canonicalUrl: string;
  robots: string;
  ogType: 'website' | 'article';
  structuredData?: Record<string, unknown> | Array<Record<string, unknown>>;
};

type SitemapRoute = {
  path: string;
  changefreq: 'daily' | 'weekly' | 'monthly';
  priority: string;
};

// Fallback lastmod for the sitemap when no explicit value is provided.
// At build time the static SEO generator passes today's date — see
// scripts/generate-static-seo-files.ts.
const SITE_LAST_MODIFIED_DATE_FALLBACK = '2026-05-05';

function formatSitemapDate(value: Date) {
  return value.toISOString().slice(0, 10);
}

// Coordonnées géographiques de Gameside Caen (Ifs).
const GAMESIDE_GEO = {
  latitude: 49.1006,
  longitude: -0.3469,
};

const TWITTER_HANDLE = '@gamesidefrance';
const FACEBOOK_URL = 'https://www.facebook.com/gamesidefrance';
const INSTAGRAM_URL = 'https://www.instagram.com/gamesidefrance/';

const SITEMAP_ROUTES: SitemapRoute[] = [
  { path: '/', changefreq: 'weekly', priority: '1.0' },
  { path: '/nos-activites/action-game', changefreq: 'weekly', priority: '0.9' },
  { path: '/nos-activites/karaoke', changefreq: 'weekly', priority: '0.9' },
  { path: '/entreprises/team-building', changefreq: 'weekly', priority: '0.8' },
  { path: '/entreprises/privatisation', changefreq: 'weekly', priority: '0.8' },
  { path: '/entreprises/seminaire', changefreq: 'weekly', priority: '0.8' },
  { path: '/infos-pratiques', changefreq: 'weekly', priority: '0.8' },
  { path: '/actualites', changefreq: 'daily', priority: '0.8' },
  { path: '/contact', changefreq: 'weekly', priority: '0.8' },
  { path: '/formules/evg-evjf', changefreq: 'weekly', priority: '0.7' },
  { path: '/formules/anniversaire-enfant', changefreq: 'weekly', priority: '0.7' },
  { path: '/mentions-legales', changefreq: 'monthly', priority: '0.2' },
  { path: '/politique-de-confidentialite', changefreq: 'monthly', priority: '0.2' },
  { path: '/politique-de-cookies-ue', changefreq: 'monthly', priority: '0.2' },
];

const KNOWN_ROUTE_SET = new Set<string>([
  ...SITEMAP_ROUTES.map((route) => route.path),
  '/admin-gameside-caen',
  '/politique-de-cookies',
]);

// Mapping route -> (CMS pageKey, breadcrumb metadata).
type RouteMeta = {
  pageKey?: keyof SiteContent['pages'];
  breadcrumb?: { name: string; href?: string }[];
  serviceName?: string;
};

const ROUTE_META: Record<string, RouteMeta> = {
  '/': { pageKey: 'home' },
  '/nos-activites/action-game': {
    pageKey: 'actionGame',
    breadcrumb: [
      { name: 'Accueil', href: '/' },
      { name: 'Nos activités' },
      { name: 'Action Game' },
    ],
    serviceName: 'Action Game',
  },
  '/nos-activites/karaoke': {
    pageKey: 'karaoke',
    breadcrumb: [
      { name: 'Accueil', href: '/' },
      { name: 'Nos activités' },
      { name: 'Karaoké' },
    ],
    serviceName: 'Karaoké privé',
  },
  '/entreprises/team-building': {
    pageKey: 'teamBuilding',
    breadcrumb: [
      { name: 'Accueil', href: '/' },
      { name: 'Entreprises' },
      { name: 'Team building' },
    ],
    serviceName: 'Team building entreprise',
  },
  '/entreprises/privatisation': {
    pageKey: 'privatisation',
    breadcrumb: [
      { name: 'Accueil', href: '/' },
      { name: 'Entreprises' },
      { name: 'Privatisation' },
    ],
    serviceName: 'Privatisation de salle',
  },
  '/entreprises/seminaire': {
    pageKey: 'seminaire',
    breadcrumb: [
      { name: 'Accueil', href: '/' },
      { name: 'Entreprises' },
      { name: 'Séminaire' },
    ],
    serviceName: 'Séminaire entreprise',
  },
  '/formules/evg-evjf': {
    pageKey: 'evgEvjf',
    breadcrumb: [
      { name: 'Accueil', href: '/' },
      { name: 'Formules' },
      { name: 'EVG / EVJF' },
    ],
    serviceName: 'Formule EVG / EVJF',
  },
  '/formules/anniversaire-enfant': {
    pageKey: 'anniversaireEnfant',
    breadcrumb: [
      { name: 'Accueil', href: '/' },
      { name: 'Formules' },
      { name: 'Anniversaire enfant' },
    ],
    serviceName: 'Anniversaire enfant',
  },
  '/infos-pratiques': {
    pageKey: 'infosPratiques',
    breadcrumb: [
      { name: 'Accueil', href: '/' },
      { name: 'Infos pratiques' },
    ],
  },
  '/actualites': {
    pageKey: 'actualites',
    breadcrumb: [
      { name: 'Accueil', href: '/' },
      { name: 'Actualités' },
    ],
  },
  '/contact': {
    pageKey: 'contact',
    breadcrumb: [
      { name: 'Accueil', href: '/' },
      { name: 'Contact' },
    ],
  },
  '/mentions-legales': {
    breadcrumb: [
      { name: 'Accueil', href: '/' },
      { name: 'Mentions légales' },
    ],
  },
  '/politique-de-confidentialite': {
    breadcrumb: [
      { name: 'Accueil', href: '/' },
      { name: 'Politique de confidentialité' },
    ],
  },
  '/politique-de-cookies-ue': {
    breadcrumb: [
      { name: 'Accueil', href: '/' },
      { name: 'Politique de cookies' },
    ],
  },
};

function escapeHtml(value: string) {
  return value
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function serializeJsonForHtml(value: unknown) {
  return JSON.stringify(value)
    .replace(/</g, '\\u003c')
    .replace(/>/g, '\\u003e')
    .replace(/&/g, '\\u0026')
    .replace(/\u2028/g, '\\u2028')
    .replace(/\u2029/g, '\\u2029');
}

function absoluteUrl(origin: string, value: string) {
  if (!value) {
    return origin;
  }

  if (/^https?:\/\//i.test(value)) {
    return value;
  }

  return `${origin}${value.startsWith('/') ? value : `/${value}`}`;
}

function normalizeHour(value: string) {
  const cleaned = value.trim().toLowerCase();
  const match = cleaned.match(/^(\d{1,2})h(?:(\d{2}))?$/);

  if (!match) {
    return '00:00';
  }

  const hours = match[1].padStart(2, '0');
  const minutes = (match[2] || '00').padStart(2, '0');
  return `${hours}:${minutes}`;
}

export function normalizePathname(pathname: string) {
  const [rawPath] = pathname.split(/[?#]/, 1);
  if (!rawPath || rawPath === '') {
    return '/';
  }

  if (rawPath.length > 1 && rawPath.endsWith('/')) {
    return rawPath.slice(0, -1);
  }

  return rawPath;
}

export function isKnownSiteRoute(pathname: string) {
  return KNOWN_ROUTE_SET.has(normalizePathname(pathname));
}

export function buildOpeningHours(content: SiteContent) {
  const dayMap: Record<string, string> = {
    Lundi: 'https://schema.org/Monday',
    Mardi: 'https://schema.org/Tuesday',
    Mercredi: 'https://schema.org/Wednesday',
    Jeudi: 'https://schema.org/Thursday',
    Vendredi: 'https://schema.org/Friday',
    Samedi: 'https://schema.org/Saturday',
    Dimanche: 'https://schema.org/Sunday',
  };

  return content.weeklyHours
    .filter((slot) => slot.time && slot.time.toLowerCase() !== 'ferme' && slot.time.toLowerCase() !== 'fermé')
    .map((slot) => {
      const [openingTime, closingTime] = slot.time.split('-').map((part) => part.trim());

      return {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: dayMap[slot.day] || slot.day,
        opens: normalizeHour(openingTime || ''),
        closes: normalizeHour(closingTime || ''),
      };
    });
}

function collectLocalBusinessImages(content: SiteContent, origin: string) {
  const candidates = [
    content.images.home.hero,
    content.images.actionGame.hero,
    content.images.actionGame.intro,
    content.images.home.sidebarFeature,
  ];

  const seen = new Set<string>();
  const urls: string[] = [];

  for (const candidate of candidates) {
    if (!candidate) continue;
    const absolute = absoluteUrl(origin, candidate);
    if (seen.has(absolute)) continue;
    seen.add(absolute);
    urls.push(absolute);
  }

  return urls;
}

function buildLocalBusinessSchema(content: SiteContent, origin: string, canonicalUrl: string) {
  const images = collectLocalBusinessImages(content, origin);

  return {
    '@context': 'https://schema.org',
    '@type': 'AmusementCenter',
    '@id': `${origin}/#localbusiness`,
    name: content.siteName,
    image: images.length > 0 ? images : undefined,
    telephone: content.phoneDisplay,
    priceRange: '€€',
    address: {
      '@type': 'PostalAddress',
      streetAddress: content.addressLines[0],
      addressLocality: 'Ifs',
      postalCode: '14123',
      addressRegion: 'Calvados',
      addressCountry: 'FR',
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: GAMESIDE_GEO.latitude,
      longitude: GAMESIDE_GEO.longitude,
    },
    url: canonicalUrl,
    hasMap: content.directionsUrl || undefined,
    openingHoursSpecification: buildOpeningHours(content),
    sameAs: [FACEBOOK_URL, INSTAGRAM_URL],
  };
}

function buildOrganizationSchema(content: SiteContent, origin: string) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': `${origin}/#organization`,
    name: content.siteName,
    url: origin,
    logo: absoluteUrl(origin, content.brand.logos.primary),
    sameAs: [FACEBOOK_URL, INSTAGRAM_URL],
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: content.phoneDisplay,
      contactType: 'customer service',
      areaServed: 'FR',
      availableLanguage: 'French',
    },
  };
}

function buildFaqSchemaFromPage(content: SiteContent, pageKey?: keyof SiteContent['pages']) {
  if (!pageKey) return null;
  const page = content.pages[pageKey];
  if (!page) return null;
  const faq = (page as Record<string, unknown>).faq as Record<string, unknown> | undefined;
  const items = getItems(faq as never, 'items');

  if (!items.length) return null;

  const mainEntity = items
    .filter((item) => item && (item.question || item.answer))
    .map((item) => ({
      '@type': 'Question',
      name: item.question || '',
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer || '',
      },
    }));

  if (!mainEntity.length) return null;

  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity,
  };
}

function buildBreadcrumbSchema(meta: RouteMeta | undefined, origin: string, canonicalUrl: string) {
  const breadcrumb = meta?.breadcrumb;
  if (!breadcrumb || breadcrumb.length === 0) return null;

  const linkedBreadcrumb = breadcrumb.filter((entry, index) => (
    index === 0 ||
    index === breadcrumb.length - 1 ||
    Boolean(entry.href)
  ));

  const itemListElement = linkedBreadcrumb.map((entry, index) => {
    const isLast = index === linkedBreadcrumb.length - 1;
    const item: Record<string, unknown> = {
      '@type': 'ListItem',
      position: index + 1,
      name: entry.name,
      item: isLast ? canonicalUrl : absoluteUrl(origin, entry.href || '/'),
    };

    return item;
  });

  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement,
  };
}

function buildServiceSchema(meta: RouteMeta | undefined, config: { description: string; url: string; image: string }, origin: string) {
  if (!meta?.serviceName) return null;

  return {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: meta.serviceName,
    description: config.description,
    url: config.url,
    image: config.image,
    serviceType: meta.serviceName,
    areaServed: {
      '@type': 'City',
      name: 'Caen',
    },
    provider: {
      '@id': `${origin}/#localbusiness`,
    },
  };
}

function resolvePageHeroImage(content: SiteContent, pageKey: keyof SiteContent['pages'], fallback: string) {
  const page = content.pages[pageKey] as Record<string, unknown> | undefined;
  const hero = page?.hero as Record<string, unknown> | undefined;
  return getText(hero as never, 'image', fallback);
}

export function getSeoConfig(pathname: string, content: SiteContent, origin: string): SeoConfig {
  const normalizedPathname = normalizePathname(pathname);
  const canonicalUrl = absoluteUrl(origin, normalizedPathname);
  const newsImage = content.news.items.find((item) => item.enabled && item.image)?.image || content.images.home.hero;
  const meta = ROUTE_META[normalizedPathname];

  const pages: Record<string, Omit<SeoConfig, 'canonicalUrl' | 'structuredData'> & { structuredData?: SeoConfig['structuredData'] }> = {
    '/': {
      title: 'Activités immersives & événements | Gameside Caen',
      description: "Activité Caen : Gameside à Ifs propose Action Game, anniversaires, EVG, EVJF et team building pour vos sorties de groupe.",
      keywords: 'activité caen, activité de groupe caen, sortie caen, action game caen, loisir caen, activité indoor caen, team building caen, anniversaire caen',
      image: content.images.home.hero,
      imageAlt: 'Activité Caen Action Game chez Gameside à Ifs',
      robots: 'index, follow',
      ogType: 'website',
    },
    '/nos-activites/action-game': {
      title: 'Action Game Caen : 20+ salles immersives | Gameside Caen',
      description: "Action Game Caen : plus de 20 salles, 600 m², défis de 3 minutes et jeu en équipe accessible sans être sportif.",
      keywords: 'action game caen, activité immersive caen, jeu en équipe caen, activité indoor caen, action game ifs, sortie groupe caen',
      image: resolvePageHeroImage(content, 'actionGame', content.images.actionGame.hero),
      imageAlt: 'Action Game Caen salle immersive Gameside',
      robots: 'index, follow',
      ogType: 'website',
    },
    '/nos-activites/karaoke': {
      title: 'Karaoké privé Caen : réservez votre salle | Gameside',
      description: 'Karaoké à Caen : réservez une salle privée chez Gameside à Ifs pour vos sorties entre amis, anniversaires, EVG, EVJF et afterworks.',
      keywords: 'karaoké caen, karaoké privé caen, salle karaoké caen, karaoké ifs, anniversaire karaoké caen, activité groupe caen',
      image: resolvePageHeroImage(content, 'karaoke', content.images.karaoke.hero),
      imageAlt: 'Groupe chantant dans une salle de karaoké privée',
      robots: 'index, follow',
      ogType: 'website',
    },
    '/entreprises/team-building': {
      title: "Team Building Caen : cohésion d'équipe | Gameside Caen",
      description: "Team building Caen : organisez une activité entreprise à Ifs avec Action Game, cohésion d’équipe et défis en groupe.",
      keywords: 'team building caen, activité entreprise caen, cohésion équipe caen, animation entreprise caen, team building ifs, séminaire caen',
      image: resolvePageHeroImage(content, 'teamBuilding', content.images.teamBuilding.hero),
      imageAlt: 'Team building Caen entreprise chez Gameside',
      robots: 'index, follow',
      ogType: 'website',
    },
    '/entreprises/privatisation': {
      title: 'Privatisation salle Caen : événements pros | Gameside Caen',
      description: 'Privatisation salle Caen : organisez près d’Ifs un événement entreprise, une soirée privée ou un groupe autour de l’Action Game.',
      keywords: 'privatisation salle caen, privatisation caen, événement entreprise caen, lieu événementiel caen, location salle ifs',
      image: resolvePageHeroImage(content, 'privatisation', content.images.privatisation.hero),
      imageAlt: 'Privatisation salle Caen chez Gameside à Ifs',
      robots: 'index, follow',
      ogType: 'website',
    },
    '/entreprises/seminaire': {
      title: 'Séminaire Caen : réunion & activité | Gameside Caen',
      description: 'Séminaire Caen : combinez réunion, activité immersive, team building et moment de cohésion chez Gameside à Ifs.',
      keywords: 'séminaire caen, salle séminaire caen, salle réunion caen, activité entreprise caen, séminaire ifs, team building caen',
      image: resolvePageHeroImage(content, 'seminaire', content.images.seminaire.hero),
      imageAlt: 'Séminaire Caen entreprise chez Gameside',
      robots: 'index, follow',
      ogType: 'website',
    },
    '/infos-pratiques': {
      title: 'Infos pratiques : horaires & accès | Gameside Caen',
      description: "Infos pratiques Gameside Caen : horaires, adresse, accès, téléphone, parking et FAQ pour préparer votre venue à Ifs.",
      keywords: 'horaires gameside caen, adresse gameside caen, infos pratiques ifs, parking gameside, action game caen adresse',
      image: resolvePageHeroImage(content, 'infosPratiques', content.images.infosPratiques.hero),
      imageAlt: 'Infos pratiques Gameside Caen accès et horaires',
      robots: 'index, follow',
      ogType: 'website',
    },
    '/actualites': {
      title: 'Actualités & événements à Caen | Gameside Caen',
      description: "Actualités Gameside Caen : événements, animations, offres groupes et nouveautés autour de l’Action Game à Ifs.",
      keywords: 'actualités gameside caen, événement caen, animations caen, événement ifs, action game caen, activité groupe caen',
      image: newsImage,
      imageAlt: 'Actualités Gameside Caen événements et infos',
      robots: 'index, follow',
      ogType: 'website',
    },
    '/contact': {
      title: 'Contact : réservation & devis groupe | Gameside Caen',
      description: 'Contact Gameside Caen : réservez une activité, demandez un devis groupe, organisez anniversaire, EVG, EVJF ou team building à Ifs.',
      keywords: 'contact gameside caen, réservation activité caen, devis groupe caen, réservation action game caen, gameside ifs',
      image: content.images.home.hero,
      imageAlt: 'Contact Gameside Caen réservation et devis',
      robots: 'index, follow',
      ogType: 'website',
    },
    '/formules/evg-evjf': {
      title: 'EVG / EVJF Caen : activité immersive | Gameside Caen',
      description: 'EVG ou EVJF Caen : organisez une activité de groupe à Ifs avec Action Game, défis en équipe et formule simple à organiser.',
      keywords: 'evg caen, evjf caen, activité evg caen, activité evjf caen, enterrement vie célibataire caen, activité groupe ifs',
      image: resolvePageHeroImage(content, 'evgEvjf', content.images.evgEvjf.hero),
      imageAlt: 'EVG EVJF Caen chez Gameside',
      robots: 'index, follow',
      ogType: 'website',
    },
    '/formules/anniversaire-enfant': {
      title: 'Anniversaire enfant Caen : la fête active | Gameside Caen',
      description: "Anniversaire enfant Caen : fêtez l'événement chez Gameside à Ifs avec Action Game, activité indoor et organisation simple.",
      keywords: 'anniversaire enfant caen, activité anniversaire enfant caen, fête anniversaire caen, activité enfant ifs, anniversaire indoor caen',
      image: resolvePageHeroImage(content, 'anniversaireEnfant', content.images.anniversaireEnfant.hero),
      imageAlt: 'Anniversaire enfant Caen chez Gameside',
      robots: 'index, follow',
      ogType: 'website',
    },
    '/admin-gameside-caen': {
      title: 'Admin Gameside | Back-office',
      description: "Interface d'administration Gameside.",
      keywords: 'gameside admin',
      image: content.images.home.hero,
      imageAlt: 'Interface admin Gameside',
      robots: 'noindex, nofollow',
      ogType: 'website',
    },
    '/mentions-legales': {
      title: 'Mentions légales | Gameside Caen',
      description: "Consultez les mentions légales du site Gameside Caen, complexe d'activités situé à Ifs près de Caen.",
      keywords: 'mentions légales gameside caen, gameside ifs',
      image: content.images.home.hero,
      imageAlt: 'Mentions légales Gameside Caen',
      robots: 'index, follow',
      ogType: 'website',
    },
    '/politique-de-confidentialite': {
      title: 'Politique de confidentialité | Gameside Caen',
      description: 'Retrouvez les informations sur la collecte, l’usage et la protection des données personnelles sur le site Gameside Caen.',
      keywords: 'politique confidentialité gameside caen, données personnelles gameside',
      image: content.images.home.hero,
      imageAlt: 'Politique de confidentialité Gameside Caen',
      robots: 'index, follow',
      ogType: 'website',
    },
    '/politique-de-cookies-ue': {
      title: 'Politique de cookies | Gameside Caen',
      description: 'Consultez la politique de cookies Gameside Caen et les informations utiles sur le suivi, la mesure et la gestion du consentement.',
      keywords: 'cookies gameside caen, politique cookies gameside',
      image: content.images.home.hero,
      imageAlt: 'Politique de cookies Gameside Caen',
      robots: 'index, follow',
      ogType: 'website',
    },
    '/politique-de-cookies': {
      title: 'Politique de cookies | Gameside Caen',
      description: 'Consultez la politique de cookies Gameside Caen et les informations utiles sur le suivi, la mesure et la gestion du consentement.',
      keywords: 'cookies gameside caen, politique cookies gameside',
      image: content.images.home.hero,
      imageAlt: 'Politique de cookies Gameside Caen',
      robots: 'noindex, follow',
      ogType: 'website',
    },
  };

  const page = pages[normalizedPathname];

  if (!page) {
    return {
      title: 'Page introuvable | Gameside Caen',
      description: 'Cette page Gameside Caen est introuvable ou n’est plus disponible.',
      keywords: 'gameside caen',
      image: content.images.home.hero,
      imageAlt: 'Gameside Caen',
      canonicalUrl,
      robots: 'noindex, follow',
      ogType: 'website',
    };
  }

  // On construit la liste des schémas structurés selon la page.
  const schemas: Array<Record<string, unknown>> = [];

  // Organization sur toutes les pages indexables (sauf admin).
  const isIndexable = !page.robots.includes('noindex');

  if (isIndexable) {
    schemas.push(buildOrganizationSchema(content, origin));
  }

  // LocalBusiness sur la home et sur infos-pratiques (pages de référence pour Google Maps).
  if (normalizedPathname === '/' || normalizedPathname === '/infos-pratiques') {
    schemas.push(buildLocalBusinessSchema(content, origin, canonicalUrl));
  }

  // BreadcrumbList sur toutes les sous-pages.
  if (isIndexable) {
    const breadcrumb = buildBreadcrumbSchema(meta, origin, canonicalUrl);
    if (breadcrumb) {
      schemas.push(breadcrumb);
    }
  }

  // FAQPage : sur toute page qui a une FAQ dans son CMS.
  if (isIndexable) {
    const faqSchema = buildFaqSchemaFromPage(content, meta?.pageKey);
    if (faqSchema) {
      schemas.push(faqSchema);
    }
  }

  // Service : sur les pages commerciales (formules, entreprises, action-game, karaoke).
  if (isIndexable && meta?.serviceName) {
    const serviceImage = absoluteUrl(origin, page.image);
    const serviceSchema = buildServiceSchema(
      meta,
      { description: page.description, url: canonicalUrl, image: serviceImage },
      origin,
    );
    if (serviceSchema) {
      schemas.push(serviceSchema);
    }
  }

  return {
    ...page,
    canonicalUrl,
    structuredData: schemas.length > 0 ? schemas : undefined,
  };
}

function buildPreconnectAndPreloadTags(imageUrl: string, isProductionImage: boolean, imageSrcSet?: string) {
  const tags = [
    '<link rel="preconnect" href="https://www.youtube-nocookie.com" crossorigin />',
    '<link rel="preconnect" href="https://www.google.com" crossorigin />',
    '<link rel="dns-prefetch" href="https://www.youtube.com" />',
  ];

  if (isProductionImage && imageUrl) {
    const responsiveAttrs = imageSrcSet
      ? ` imagesrcset="${escapeHtml(imageSrcSet)}" imagesizes="100vw"`
      : '';
    tags.push(`<link rel="preload" as="image" href="${escapeHtml(imageUrl)}"${responsiveAttrs} fetchpriority="high" />`);
  }

  return tags;
}

export function buildSeoHead(pathname: string, content: SiteContent, origin: string) {
  const config = getSeoConfig(pathname, content, origin);
  const imageUrl = absoluteUrl(origin, toSeoImageUrl(config.image, config.imageAlt, config.title, { width: 1200, quality: 78 }) || config.image);
  const imageSrcSet = toSeoImageSrcSet(config.image, config.imageAlt, config.title);
  const structuredDataMarkup = config.structuredData
    ? `<script id="gameside-structured-data" type="application/ld+json">${serializeJsonForHtml(Array.isArray(config.structuredData) ? config.structuredData : [config.structuredData])}</script>`
    : '';

  const preconnectTags = buildPreconnectAndPreloadTags(imageUrl, Boolean(config.image), imageSrcSet);

  return [
    '<meta charset="UTF-8" />',
    '<meta name="viewport" content="width=device-width, initial-scale=1.0, viewport-fit=cover" />',
    '<meta name="apple-mobile-web-app-capable" content="yes" />',
    '<meta name="mobile-web-app-capable" content="yes" />',
    '<meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />',
    '<meta name="apple-mobile-web-app-title" content="Gameside Caen" />',
    '<meta name="format-detection" content="telephone=no" />',
    '<link rel="apple-touch-icon" sizes="180x180" href="/apple-touch-icon.png" />',
    '<link rel="icon" type="image/png" sizes="32x32" href="/favicon-32x32.png" />',
    '<link rel="icon" type="image/png" sizes="16x16" href="/favicon-16x16.png" />',
    '<link rel="mask-icon" href="/safari-pinned-tab.svg" color="#1f2025" />',
    '<link rel="shortcut icon" href="/favicon.ico" />',
    '<meta name="theme-color" media="(prefers-color-scheme: light)" content="#facc15" />',
    '<meta name="theme-color" media="(prefers-color-scheme: dark)" content="#14151f" />',
    ...preconnectTags,
    `<title>${escapeHtml(config.title)}</title>`,
    `<meta name="description" content="${escapeHtml(config.description)}" />`,
    `<meta name="keywords" content="${escapeHtml(config.keywords)}" />`,
    `<meta name="robots" content="${escapeHtml(config.robots)}" />`,
    `<meta name="author" content="${escapeHtml(content.siteName)}" />`,
    `<link rel="canonical" href="${escapeHtml(config.canonicalUrl)}" />`,
    `<meta property="og:title" content="${escapeHtml(config.title)}" />`,
    `<meta property="og:description" content="${escapeHtml(config.description)}" />`,
    `<meta property="og:type" content="${escapeHtml(config.ogType)}" />`,
    `<meta property="og:url" content="${escapeHtml(config.canonicalUrl)}" />`,
    `<meta property="og:image" content="${escapeHtml(imageUrl)}" />`,
    `<meta property="og:image:alt" content="${escapeHtml(config.imageAlt)}" />`,
    `<meta property="og:site_name" content="${escapeHtml(content.siteName)}" />`,
    '<meta property="og:locale" content="fr_FR" />',
    '<meta name="twitter:card" content="summary_large_image" />',
    `<meta name="twitter:site" content="${TWITTER_HANDLE}" />`,
    `<meta name="twitter:creator" content="${TWITTER_HANDLE}" />`,
    `<meta name="twitter:title" content="${escapeHtml(config.title)}" />`,
    `<meta name="twitter:description" content="${escapeHtml(config.description)}" />`,
    `<meta name="twitter:image" content="${escapeHtml(imageUrl)}" />`,
    `<meta name="twitter:image:alt" content="${escapeHtml(config.imageAlt)}" />`,
    structuredDataMarkup,
  ].filter(Boolean).join('\n    ');
}

export function buildRobotsTxt(origin: string) {
  return [
    'User-agent: *',
    'Allow: /',
    'Disallow: /admin',
    'Disallow: /admin-gameside-caen',
    'Disallow: /api/',
    `Sitemap: ${absoluteUrl(origin, '/sitemap.xml')}`,
    '',
  ].join('\n');
}

export function buildSitemapXml(origin: string, content: SiteContent, options: { lastmod?: string } = {}) {
  const lastmod = options.lastmod || SITE_LAST_MODIFIED_DATE_FALLBACK;
  const urls = SITEMAP_ROUTES.map((route) => {
    const config = getSeoConfig(route.path, content, origin);
    const imageUrl = absoluteUrl(origin, toSeoImageUrl(config.image, config.imageAlt, config.title) || config.image);
    const imageCaption = escapeHtml(config.imageAlt);

    return [
      '  <url>',
      `    <loc>${escapeHtml(config.canonicalUrl)}</loc>`,
      `    <lastmod>${lastmod}</lastmod>`,
      `    <changefreq>${route.changefreq}</changefreq>`,
      `    <priority>${route.priority}</priority>`,
      '    <image:image>',
      `      <image:loc>${escapeHtml(imageUrl)}</image:loc>`,
      `      <image:caption>${imageCaption}</image:caption>`,
      '    </image:image>',
      '  </url>',
    ].join('\n');
  });

  return [
    '<?xml version="1.0" encoding="UTF-8"?>',
    '<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9" xmlns:image="http://www.google.com/schemas/sitemap-image/1.1">',
    ...urls,
    '</urlset>',
    '',
  ].join('\n');
}

export function serializeSiteContentState(content: SiteContent) {
  return `<script>window.__GAMESIDE_SITE_CONTENT__ = ${serializeJsonForHtml(content)};</script>`;
}

// Liste des routes connues à pré-rendre côté static (build), y compris les routes
// non listées dans le sitemap public (ex. /admin-gameside-caen en noindex).
export const PRERENDERABLE_ROUTES: string[] = [
  ...SITEMAP_ROUTES.map((route) => route.path),
  '/admin-gameside-caen',
];

// Injection commune entre le SSR (server/index.ts) et le pré-rendu statique
// (scripts/generate-static-seo-files.ts) — garde un seul comportement.
//
// Remplace le bloc <!-- SEO_FALLBACK_START --> ... <!-- SEO_FALLBACK_END -->
// d'index.html par le head SEO dynamique (title, description, canonical,
// OG, JSON-LD propres à la route). Le bloc fallback du template sert
// uniquement en dev sans SSR ; en prod/build il est éliminé.
export function injectHtmlTemplate(
  template: string,
  headMarkup: string,
  siteContentScript: string,
) {
  return template
    .replace(/<html(?:\s+lang="[^"]*")?>/i, '<html lang="fr">')
    .replace(
      /<!--\s*SEO_FALLBACK_START\s*-->[\s\S]*?<!--\s*SEO_FALLBACK_END\s*-->/i,
      headMarkup,
    )
    // Compat : si jamais le template ne contient pas le bloc marqué (ex. test
    // unitaire qui passerait une string customisée), on retombe sur l'ancien
    // placeholder ponctuel.
    .replace(/<meta name="x-app-seo-placeholder" content="app-seo"\s*\/?>/i, headMarkup)
    .replace(/<script id="app-site-content-placeholder"><\/script>/i, siteContentScript);
}

// Génère le contenu de llms.txt — description du site pour les assistants IA.
// La structure suit la convention émergente https://llmstxt.org/.
export function buildLlmsTxt(content: SiteContent, origin: string) {
  const lines = [
    '# Gameside Caen',
    '',
    `> Activité immersive à Ifs (Caen métropole). Plus de 20 salles d'Action Game sur 600 m², défis de 3 minutes en équipe. Adapté aux groupes : EVG, EVJF, anniversaires enfant, team building, séminaire, privatisation.`,
    '',
    '## Coordonnées',
    '',
    `- Adresse : ${(content.addressLines || []).join(', ')}`,
    `- Téléphone : ${content.phoneDisplay || ''}`,
    `- Site : ${origin}`,
    '',
    '## Pages clés',
    '',
    `- [Accueil](${origin}/) : Vue d'ensemble de Gameside Caen.`,
    `- [Action Game](${origin}/nos-activites/action-game) : Plus de 20 salles immersives, défis de 3 minutes en équipe.`,
    `- [Team building](${origin}/entreprises/team-building) : Activité de cohésion pour entreprises à Caen.`,
    `- [Séminaire](${origin}/entreprises/seminaire) : Combinaison réunion + activité immersive.`,
    `- [Privatisation](${origin}/entreprises/privatisation) : Privatisation de salle pour événement pro ou privé.`,
    `- [EVG / EVJF](${origin}/formules/evg-evjf) : Enterrement de vie de célibataire à Caen.`,
    `- [Anniversaire enfant](${origin}/formules/anniversaire-enfant) : Fête d'anniversaire enfant indoor.`,
    `- [Infos pratiques](${origin}/infos-pratiques) : Horaires, accès, parking, FAQ.`,
    `- [Actualités](${origin}/actualites) : Événements, animations, nouveautés.`,
    `- [Contact](${origin}/contact) : Réservation, devis groupe.`,
    '',
    '## À propos',
    '',
    `- Localisation : Ifs, à 10 minutes du centre de Caen (Calvados, Normandie).`,
    `- Type d'activité : Centre de divertissement immersif (catégorie escape game / action game).`,
    `- Réservation : par téléphone ${content.phoneDisplay || ''} ou via le formulaire de contact.`,
    `- Réseaux : Facebook /gamesidefrance, Instagram @gamesidefrance.`,
    '',
    '## Optional',
    '',
    `- [Sitemap XML](${origin}/sitemap.xml)`,
    `- [Politique de confidentialité](${origin}/politique-de-confidentialite)`,
    `- [Mentions légales](${origin}/mentions-legales)`,
    '',
  ];
  return lines.join('\n');
}
